import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscribable } from '../models/base/subscribable';
import { AbstractControl, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { BehaviorSubject, merge, Observable, Subject } from 'rxjs';
import { CANADA_COUNTRY_ID, USA_COUNTRY_ID } from '../consts/consts';
import { distinctUntilChanged, map, skip, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { GeographyService } from './geography/geography.service';
import {
  canadaPostalCodeFormatValidator,
  canadaPostalCodeLengthValidator,
  canadaPostalCodeProvinceValidator
} from '../validators/canada-postal-code.validator';

const USA_POSTAL_CODE_MAX_LENGTH = 10;

const STATE_AND_POSTAL_CODE_REQUIRED_COUNTRY_IDS = [CANADA_COUNTRY_ID, USA_COUNTRY_ID];

@Injectable()
export class AddressFormService extends Subscribable {
  private destroyRef = inject(DestroyRef);
  private geographyService = inject(GeographyService);

  private managedControlsChanged = new Subject<void>();

  private _selectedCountryId = new BehaviorSubject<number>(CANADA_COUNTRY_ID);
  public selectedCountryId$ = this._selectedCountryId as Observable<number>;

  /**
   * Province/State/Region and Postal Code/ZIP are only mandatory for Canada and the
   * United States;
   */
  public stateAndPostalCodeRequired$ = this.selectedCountryId$.pipe(
    map(countryId => STATE_AND_POSTAL_CODE_REQUIRED_COUNTRY_IDS.includes(countryId))
  );

  public stateAndPostalCodeOptional$ = this.stateAndPostalCodeRequired$.pipe(map(required => !required));

  public manageCountryStateControls(countryControl: FormControl, stateControl: FormControl): void {
    if (stateControl.disabled) {
      return;
    }

    this._selectedCountryId.next(countryControl.value ?? CANADA_COUNTRY_ID);

    countryControl.valueChanges.subscribeWhileAlive({
      owner: this,
      next: () => {
        stateControl.setValue(null, { emitEvent: false });
      }
    });

    countryControl.valueChanges.pipe(startWith(countryControl.value)).subscribeWhileAlive({
      owner: this,
      next: value => {
        this._selectedCountryId.next(value ?? CANADA_COUNTRY_ID);
        if (value && ![USA_COUNTRY_ID, CANADA_COUNTRY_ID].includes(value)) {
          stateControl.disable({ emitEvent: false });
        } else {
          stateControl.enable({ emitEvent: false });
        }
      }
    });
  }

  public manageInternationalAddressControls(
    countryControl: FormControl<number | null>,
    stateControl: FormControl<number | null>,
    postalCodeControl: FormControl<string | null>
  ): void {
    this.managedControlsChanged.next();

    countryControl.valueChanges
      .pipe(
        startWith(countryControl.value),
        distinctUntilChanged(),
        skip(1),
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.managedControlsChanged)
      )
      .subscribe(() => stateControl.setValue(null, { emitEvent: false }));

    countryControl.valueChanges
      .pipe(
        startWith(countryControl.value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.managedControlsChanged)
      )
      .subscribe(countryId => {
        const resolvedCountryId = countryId ?? CANADA_COUNTRY_ID;
        this._selectedCountryId.next(resolvedCountryId);

        this.setRequired(stateControl, STATE_AND_POSTAL_CODE_REQUIRED_COUNTRY_IDS.includes(resolvedCountryId));
      });

    this.selectedCountryAndStateCode$(countryControl, stateControl)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.managedControlsChanged))
      .subscribe(({ countryId, stateCode }) => this.applyPostalCodeValidators(postalCodeControl, countryId, stateCode));
  }

  private selectedCountryAndStateCode$(
    countryControl: FormControl<number | null>,
    stateControl: FormControl<number | null>
  ): Observable<{ countryId: number; stateCode: string | null }> {
    // disable() and enable() re-emit unchanged values; reacting to those mid-disable leaves the parent group's status stale.
    return merge(countryControl.valueChanges, stateControl.valueChanges).pipe(
      startWith(null),
      map(() => ({ countryId: countryControl.value ?? CANADA_COUNTRY_ID, stateId: stateControl.value })),
      distinctUntilChanged(
        (previous, current) => previous.countryId === current.countryId && previous.stateId === current.stateId
      ),
      switchMap(({ countryId, stateId }) =>
        this.geographyService.statesForCountry(countryId).pipe(
          map(states => ({
            countryId,
            stateCode: states.find(state => state.id === stateId)?.stateCode ?? null
          }))
        )
      )
    );
  }

  private applyPostalCodeValidators(
    postalCodeControl: FormControl<string | null>,
    countryId: number,
    stateCode: string | null
  ): void {
    const validators: ValidatorFn[] = [];

    if (STATE_AND_POSTAL_CODE_REQUIRED_COUNTRY_IDS.includes(countryId)) {
      validators.push(Validators.required);
    }

    if (countryId === CANADA_COUNTRY_ID) {
      validators.push(
        canadaPostalCodeLengthValidator(),
        canadaPostalCodeFormatValidator(),
        canadaPostalCodeProvinceValidator(stateCode)
      );
    } else if (countryId === USA_COUNTRY_ID) {
      validators.push(Validators.minLength(1), Validators.maxLength(USA_POSTAL_CODE_MAX_LENGTH));
    }

    postalCodeControl.setValidators(validators);
    postalCodeControl.updateValueAndValidity({ emitEvent: false });
  }

  private setRequired(control: AbstractControl, required: boolean): void {
    if (required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
    control.updateValueAndValidity({ emitEvent: false });
  }
}
